<script setup>
// Template picker as a grid of thumbnails.
//
// A <select> of template names is the wrong control here: the names are slugs the marketing
// team invented (`relatorio_4aedicao_paraconfirmadosnaopresentes`), and the operator is
// choosing the artwork a whole segment is about to receive. Meta gives us the approved
// sample image for media headers, so the card shows the real thing; text-only templates
// fall back to their body copy, which is all there is to see.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  templates: { type: Array, default: () => [] },
  modelValue: { type: String, default: '' },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const search = ref('');

// Meta's sample URLs are signed and occasionally 403 after the list was fetched. A broken
// <img> in a picker reads as "this template is broken", so a failed load degrades to the
// same text card a header-less template gets.
const brokenPreviews = ref(new Set());

function bodyText(template) {
  const body = (template.components || []).find(c => c.type === 'BODY');
  return body?.text || '';
}

// Template bodies are written for a chat bubble: blank lines between paragraphs, a
// greeting on its own line. Clamped to two lines in a card, that whitespace is most of
// what you get, so it collapses to a single run before truncating.
function bodyExcerpt(template) {
  return bodyText(template).replace(/\s+/g, ' ').trim();
}

function previewUrl(template) {
  if (brokenPreviews.value.has(template.name)) return '';
  return template.preview_image_url || '';
}

function onPreviewError(template) {
  // Reassigning rather than mutating: a Set mutation is not reactive on its own.
  brokenPreviews.value = new Set(brokenPreviews.value).add(template.name);
}

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return props.templates;
  return props.templates.filter(template =>
    `${template.name} ${bodyText(template)}`.toLowerCase().includes(term)
  );
});
</script>

<template>
  <div>
    <input
      v-if="templates.length > 6"
      v-model="search"
      type="search"
      class="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand"
      :placeholder="t('OUTBOUNDS.TEMPLATE.SEARCH')"
    />

    <p v-if="loading" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.TEMPLATE.LOADING') }}
    </p>

    <p v-else-if="!templates.length" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.TEMPLATE.EMPTY') }}
    </p>

    <p v-else-if="!filtered.length" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.TEMPLATE.NO_MATCH') }}
    </p>

    <div
      v-else
      class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
      role="radiogroup"
      :aria-label="t('OUTBOUNDS.TEMPLATE.TITLE')"
    >
      <button
        v-for="template in filtered"
        :key="template.name"
        type="button"
        role="radio"
        :aria-checked="modelValue === template.name"
        class="flex flex-col overflow-hidden text-left transition-colors border rounded-xl bg-n-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        :class="
          modelValue === template.name
            ? 'border-n-brand ring-2 ring-n-brand/40'
            : 'border-n-weak hover:border-n-slate-5'
        "
        @click="emit('update:modelValue', template.name)"
      >
        <!-- Uniform 4:3 band so the grid scans cleanly, but the artwork is never cropped
             to fit it: Meta's samples are mostly 1280x1600 portrait, and `object-cover`
             here cut about half of every one. The art is contained and the leftover space
             is filled by a blurred copy of the same file — same URL, so no second
             request — instead of a flat grey bar. -->
        <span
          class="relative flex items-center justify-center w-full overflow-hidden aspect-[4/3] bg-n-alpha-2"
        >
          <template v-if="previewUrl(template)">
            <img
              :src="previewUrl(template)"
              alt=""
              aria-hidden="true"
              class="absolute inset-0 object-cover scale-110 size-full blur-xl opacity-40"
            />
            <img
              :src="previewUrl(template)"
              :alt="template.name"
              loading="lazy"
              class="relative object-contain size-full"
              @error="onPreviewError(template)"
            />
          </template>

          <!-- No artwork to show, so the copy becomes the artwork rather than sitting in
               a corner as fine print. -->
          <span
            v-else
            class="flex items-center w-full h-full p-4 text-xs leading-relaxed text-left text-n-slate-11 line-clamp-5"
          >
            {{ bodyExcerpt(template) || t('OUTBOUNDS.TEMPLATE.NO_PREVIEW') }}
          </span>

          <span
            v-if="template.header_format && template.header_format !== 'IMAGE'"
            class="absolute px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded-full top-2 left-2 bg-n-solid-1/85 text-n-slate-11"
          >
            {{ template.header_format }}
          </span>

          <span
            v-if="modelValue === template.name"
            class="absolute flex items-center justify-center rounded-full top-2 right-2 size-6 bg-n-brand"
          >
            <i class="text-white i-lucide-check size-4" />
          </span>
        </span>

        <span
          class="flex flex-col flex-1 gap-1.5 p-3 border-t border-n-weak min-w-0"
        >
          <span class="flex items-start gap-2">
            <span
              class="flex-1 text-sm font-medium leading-snug break-words text-n-slate-12 line-clamp-2"
              :title="template.name"
            >
              {{ template.name }}
            </span>
            <span
              v-if="template.language"
              class="px-1.5 py-0.5 text-[10px] shrink-0 rounded bg-n-alpha-1 text-n-slate-10"
            >
              {{ template.language }}
            </span>
          </span>

          <!-- The body shows on every card, not just the ones without artwork. Two
               templates can share the same image and differ only in the copy. -->
          <span
            v-if="bodyExcerpt(template)"
            class="text-xs leading-relaxed text-n-slate-10 line-clamp-2"
          >
            {{ bodyExcerpt(template) }}
          </span>
        </span>
      </button>
    </div>
  </div>
</template>
